import axiosInstance from '../app/axios';
import { store } from '../app/store';
import { currentAdminAsync, removeCurrentAdminValue } from '../features/admin/currentAdminSlice';
import { addNewAuth, removeAuth } from '../features/auth/authSlice';
import DefaultPaginationProps from '../propInterfaces/defaultPaginationProps';
import PutAdminProps from '../propInterfaces/putAdminProps';
import UpdatePartnerPriceTableProps from '../propInterfaces/updatePartnerPriceTableProps';
import NewAdminDTO from '../shared/dtos/newAdminDTO';
import ProviderAdminDTO from '../shared/dtos/providerAdminDTO';
import { HandleLoginHelper } from '../utils/helpers/verifyLoginHelper';

export const getAdmins = async ({
  page,
  take,
  search: searchText,
}: DefaultPaginationProps) => {
  const result = await axiosInstance.post(
    `admins/orderby/type?page=${page}&take=${take}`,
    {
      searchText,
    },
  );
  return result.data;
};

export const postAdmin = async (newAdminDTO: NewAdminDTO) => {
  const result = await axiosInstance.post(`admins`, newAdminDTO);
  return result.data;
};

export const deleteAdmin = async (id: number) => {
  const result = await axiosInstance.delete(`admins/${id}`);
  return result.data;
};

export const getAdmin = async (id: number, byToken?: boolean) => {
  const result = await axiosInstance.get(`admins/${byToken ? "by/token" : id}`);
  return result.data;
};

export const putAdmin = async ({ id, newAdminDTO }: PutAdminProps) => {
  const result = await axiosInstance.put(`admins/${id}`, newAdminDTO);
  return result.data;
};

export const updatePartnerPriceTable = async ({
  id,
  fileUrl,
}: UpdatePartnerPriceTableProps) => {
  const result = await axiosInstance.put(`admins/${id}/pricetable`, { fileUrl });
  return result.data;
};

export const patchAdminPassword = async (id: number, newPassword: string) => {
  const result = await axiosInstance.patch(`admins/${id}/password`, {
    newPassword: newPassword,
  });
  return result.data;
};

export const getAdminsList = async ({
  page,
  take,
  filters,
}: DefaultPaginationProps) => {
  const result = await axiosInstance.get(`admins`, {
    params: { page, take, filters: JSON.stringify(filters) },
    headers: { "x-hide-success": true },
  });
  return result.data;
};

export const getAdminPaginatedDocuments = async ({
  page,
  take,
  search,
  startDate,
  endDate,
}: DefaultPaginationProps) => {
  const result = await axiosInstance.post(
    `admin/trip/report/admin/document?page=${page}&take=${take}`,
    {
      search,
      startDate,
      endDate,
    },
  );

  return result.data;
};

export const getAdminEmails = async ({
  startDate,
  endDate,
}: DefaultPaginationProps) => {
  const result = await axiosInstance.post(`admins/emails`, {
    startDate: startDate,
    endDate: endDate,
  });
  return result.data;
};

export const getAdminExcel = async ({
  search,
  startDate,
  endDate,
}: DefaultPaginationProps) => {
  const result = await axiosInstance.post(
    `user-request/report/statement/excel/admin`,
    {
      search,
      startDate,
      endDate,
    },
  );

  return result.data;
};

export const updateAdvancedFlag = async (adminId: number, active: boolean) => {
  const result = await axiosInstance.post(`admins/${adminId}/advanced`, {
    active,
  });

  return result.data;
};

export const updateAdminPartner = async (id: number, access: boolean) => {
  return await axiosInstance.patch(`admins/${id}/partner`, { access });
};

export const updateAdminPartnerFee = async (partnerFee: number) => {
  return await axiosInstance.patch(`admins/partner/fee`, { partnerFee });
};

export const getAllAdmins = async () => {
  try {
    const result = await axiosInstance.get(`admins/all`);
    return result?.data ?? [];
  } catch (error) {
    console.error('Error fetching all admins:', error);
    return [];
  }
};

export const updateAdminWallet = async (id: number, access: boolean) => {
  return await axiosInstance.patch(`admins/${id}/wallet`, { access });
};

export const getAdminsExcel = async () => {
  const result = await axiosInstance.get(`admins/information/excel`);
  return result.data.file;
};

export const getAdminInformation = async () => {
  const result = await axiosInstance.get(`admins/information`);
  return result.data;
};

export const sendWhatsappToPassenger = async (
  data: Array<any>,
  notificationTypeId: number,
) => {
  const result = await axiosInstance.post(`admins/notify/passenger`, {
    data,
    notificationTypeId,
  });
  return result.data;
};

export const updateIsManualEntryAuthorizedFlag = async (
  id: number,
  flag: boolean,
) => {
  return await axiosInstance.patch(`admins/${id}/ismanualentryauthorized`, {
    flag,
  });
};

export const updateSendWhatsappMessageFlag = async (
  id: number,
  flag: boolean,
) => {
  return await axiosInstance.patch(`admins/${id}/sendwhatsappmessage`, {
    flag,
  });
};

export const getAdminsForSelect = async () => {
  const result = await axiosInstance.get(`admins/panel/select`);
  return result.data;
};

export const impersonateAdminLogin = async (adminId: number) => {
  const result = await axiosInstance.get(`admins/${adminId}/impersonate`)
    .then(async (response: any) => {
      const { token } = response.data;
      const validLogin = HandleLoginHelper(token);

      if (validLogin) {
        store.dispatch(removeAuth());
        store.dispatch(removeCurrentAdminValue());
        store.dispatch(addNewAuth(response.data));
        await store.dispatch(currentAdminAsync(token));
        return response;
      }

      return response.data;
    });

  return result;
};

export const updateAirportQrAllowed = async (id: number, flag: boolean) => {
  return await axiosInstance.patch(`admins/${id}/airportqrallowed`, { flag });
};

export const updateQrOptionalAddressAllowed = async (
  id: number,
  flag: boolean,
) => {
  return await axiosInstance.patch(`admins/${id}/qroptionaladdressallowed`, {
    flag,
  });
};

export const updateShowLogo = async (id: number, flag: boolean) => {
  return await axiosInstance.patch(`admins/${id}/showlogo`, { flag });
};

export const updateGuestTripsEnabled = async (id: number, flag: boolean) => {
  return await axiosInstance.patch(`admins/${id}/guesttripsenabled`, { flag });
};

export const createAndAssignProvider = async (
  tripId: number,
  provider: ProviderAdminDTO,
  totalTripPrice?: number,
) => {
  return await axiosInstance.post(
    `admin/trip/${tripId}/assign/provider/temporal`,
    { provider, totalTripPrice },
  );
};

export const getVoucherPreview = async (id: number) => {
  const result = await axiosInstance.get(`admins/${id}/voucher-preview`);
  return result.data;
};

export const getCountries = async () => {
  const result = await axiosInstance.get("countries");
  return result.data;
};
